import { TILE } from '@/gfx/tiles';
import { dist, angleDiff, facingFromVec, TAU } from '@/engine/math';
import { hasLos, visibility, type VisResult } from '@/gfx/lighting';
import { pointInPolygon } from '@/engine/math';
import { roomAtPx, type Floor } from '@/world/builder';
import type { FloorId } from '@/world/types';
import type { Game } from './game';
import type { Character, Monster, Noise, SearchStep } from './entities';
import { findPath } from './nav';
import { moveBox, boxFree, tileOf, tileCenter } from './physics';
import { stairsToward } from './routing';
import { planPassage, passageEntry, enterPassage, updatePassage, passageOpacity } from './passages';
import { updatePacing } from './pacing';
import { stairSoundDistance } from './acoustics';

export const MON = {
  patrol: 44,
  investigate: 72,
  chase: 108,
  carry: 86,
  search: 60,
  catchDist: 13,
  swipeDist: 26,
  pullDist: 90,
  hearMul: 1.0,
  /** Seconds it keeps pressing after losing sight, heading where the target was going. */
  pursuit: 2.0,
  /** How far ahead of the last sighting it assumes the target ran (seconds of observed velocity). */
  lead: 0.8,
  /** Cover this close to where the trail ended gets checked. */
  coverRadius: 110,
  /** Seconds a recent contact keeps patrols lingering near it. */
  lingerTime: 120,
};

/** Rooms (by floor) the monster wanders between. Missing rooms are skipped. */
const PATROL: Array<{ floor: FloorId; room: string; w: number }> = [
  { floor: 'ground', room: 'corridor', w: 3 }, { floor: 'ground', room: 'ballroom', w: 2 }, { floor: 'ground', room: 'dining', w: 1 },
  { floor: 'ground', room: 'library', w: 1 }, { floor: 'ground', room: 'gallery1', w: 2 }, { floor: 'ground', room: 'hall', w: 1 }, { floor: 'ground', room: 'westcorr', w: 1 },
  { floor: 'ground', room: 'kitchen', w: 1 }, { floor: 'ground', room: 'conservatory', w: 1 },
  { floor: 'second', room: 'wcorr2', w: 2 }, { floor: 'second', room: 'landing', w: 1 }, { floor: 'second', room: 'ecorr2', w: 2 }, { floor: 'second', room: 'gallery2', w: 1 },
  { floor: 'basement', room: 'btunnel1', w: 2 }, { floor: 'basement', room: 'cellar', w: 1 }, { floor: 'basement', room: 'btunnel2', w: 1 },
  { floor: 'third', room: 'corr3', w: 2 }, { floor: 'third', room: 'dollroom', w: 1 },
  { floor: 'attic', room: 'lair', w: 2 }, { floor: 'attic', room: 'apass1', w: 1 },
];

export function monsterDoorRule(g: Game): (d: import('@/world/builder').DoorEnt) => boolean {
  return (d) => {
    if (d.kind === 'exit') return false;
    const m = g.monster, c = g.leader;
    const x = (d.x + (d.orient === 'h' ? d.w / 2 : 0.5)) * TILE;
    const y = (d.y + (d.orient === 'v' ? d.w / 2 : 0.5)) * TILE;
    const cue = () => {
      if (!c || c.floor !== m.floor) return;
      const distance = dist(c.x, c.y, x, y);
      if (distance >= 320) return;
      g.audio.door(1 - distance / 360, Math.max(-1, Math.min(1, (x - c.x) / 180)));
      if (!g.message && distance < 200) g.say('닫힌 문이 덜컹거린다…', 2);
    };
    if (!d.locked && (d.kind !== 'secret' || d.revealed)) {
      if (!d.open) { g.openDoor(d, null); cue(); }
      return true;
    }
    // Locked barriers still protect progression. The shadow squeezes through only
    // after a visible rattle, giving a player on the other side time to move away.
    if (m.doorWait?.id !== d.id || m.doorWait.floor !== m.floor) {
      m.doorWait = { id: d.id, floor: m.floor, t: 0.75, x, y };
      d.anim = 0.9; cue();
      return false;
    }
    if (m.doorWait.t > 0) return false;
    d.anim = 0.3;
    return true;
  };
}

function roomCenter(f: Floor, roomId: string): [number, number] | null {
  const r = f.roomById.get(roomId);
  if (!r) return null;
  return [(r.x + r.w / 2) * TILE, (r.y + r.h / 2) * TILE];
}

export function pickPatrol(g: Game, m: Monster): { floor: FloorId; x: number; y: number } {
  const cands = PATROL.filter((p) => g.mansion.floors.has(p.floor) && g.mansion.get(p.floor).roomById.has(p.room));
  const sameFloor = cands.filter((p) => p.floor === m.floor);
  const pool = g.rng.chance(0.7) && sameFloor.length ? sameFloor : cands;
  // After a recent sighting or a clear noise it lingers: rooms near that contact come up far more often.
  const contact = m.lastContact && g.time - m.lastContact.t < MON.lingerTime ? m.lastContact : null;
  const weight = (p: { floor: FloorId; room: string; w: number }) => {
    if (!contact || p.floor !== contact.floor) return p.w;
    const c = roomCenter(g.mansion.get(p.floor), p.room);
    return c && dist(c[0], c[1], contact.x, contact.y) < 450 ? p.w * 4 : p.w;
  };
  let total = 0;
  for (const p of pool) total += weight(p);
  let r = g.rng.float(0, total);
  let pick = pool[0];
  for (const p of pool) {
    r -= weight(p);
    if (r <= 0) { pick = p; break; }
  }
  const f = g.mansion.get(pick.floor);
  const room = f.roomById.get(pick.room)!;
  // random free tile in the room
  for (let i = 0; i < 20; i++) {
    const tx = g.rng.int(room.x, room.x + room.w - 1), ty = g.rng.int(room.y, room.y + room.h - 1);
    if (!f.solid[ty * f.w + tx]) return { floor: pick.floor, x: tileCenter(tx), y: tileCenter(ty) };
  }
  const [cx, cy] = roomCenter(f, pick.room)!;
  return { floor: pick.floor, x: cx, y: cy };
}

/** Spawn far from the entrance. */
export function spawnMonster(g: Game): void {
  const m = g.monster;
  for (let i = 0; i < 30; i++) {
    const p = pickPatrol(g, m);
    if (p.floor === 'ground' && dist(p.x, p.y, 64 * TILE, 62 * TILE) < 500) continue;
    m.floor = p.floor; m.x = p.x; m.y = p.y;
    break;
  }
  m.state = 'patrol';
  m.sightAge = 999; m.witnessedHide = null;
  m.transit = null; m.passagePlan = null; m.passageCooldown = 20;
  m.doorWait = null;
  m.quietTime = -20; m.pressureCooldown = 0;
  m.seenAt = null; m.seenVel = null; m.lastContact = null; m.searchPlan = null; m.searchStep = null; m.lookTimer = 0;
  m.poi = pickPatrol(g, m);
  m.path = null;
}

/** Where a target that just vanished is assumed to be: the last sighting carried on by its observed velocity. */
export function predictedPoint(m: Monster): { floor: FloorId; x: number; y: number } | null {
  if (!m.lastSeen) return null;
  const v = m.seenVel;
  if (!v || Math.hypot(v.vx, v.vy) < 25) return m.lastSeen;
  return { floor: m.lastSeen.floor, x: m.lastSeen.x + v.vx * MON.lead, y: m.lastSeen.y + v.vy * MON.lead };
}

/**
 * Plan a search around the spot where the trail went cold: cover within reach first (the
 * nearest hiding furniture, checked one by one), then the exits of that room in the direction
 * the target was heading, then a couple of random looks. Nothing here consults a hidden actor.
 */
export function planSearch(g: Game, m: Monster, f: Floor, origin: { x: number; y: number }, vel: { vx: number; vy: number } | null): SearchStep[] {
  const steps: SearchStep[] = [];
  const doorRule = (d: import('@/world/builder').DoorEnt) => d.open || !d.locked && (d.kind !== 'secret' || d.revealed);
  const cover: Array<SearchStep & { d: number }> = [];
  for (const p of f.props) {
    if (!p.kind.hide) continue;
    const left = p.x * TILE, top = p.y * TILE, right = left + p.kind.fw * TILE, bottom = top + p.kind.fh * TILE;
    const cx = (left + right) / 2, cy = (top + bottom) / 2;
    const d = dist(origin.x, origin.y, cx, cy);
    if (d > MON.coverRadius) continue;
    let best: { x: number; y: number; d: number } | null = null;
    for (const [ex, ey] of [[cx, bottom + 8], [left - 8, cy], [right + 8, cy], [cx, top - 8]]) {
      if (roomAtPx(f, ex, ey)?.id !== p.room || !boxFree(f, ex, ey, doorRule, 6, 4)) continue;
      const dd = dist(origin.x, origin.y, ex, ey);
      if (!best || dd < best.d) best = { x: ex, y: ey, d: dd };
    }
    if (best) cover.push({ x: best.x, y: best.y, kind: 'hide', prop: p.id, look: 0.9, d });
  }
  cover.sort((a, b) => a.d - b.d);
  for (const c of cover.slice(0, 3)) steps.push({ x: c.x, y: c.y, kind: c.kind, prop: c.prop, look: c.look });
  const room = roomAtPx(f, origin.x, origin.y);
  if (room) {
    const exits = f.doors.filter((d) => d.rooms.includes(room.id) && doorRule(d)).map((d) => {
      const x = (d.x + (d.orient === 'h' ? d.w / 2 : 0.5)) * TILE, y = (d.y + (d.orient === 'v' ? d.w / 2 : 0.5)) * TILE;
      const dx = x - origin.x, dy = y - origin.y, len = Math.hypot(dx, dy) || 1;
      const along = vel && Math.hypot(vel.vx, vel.vy) > 25 ? (dx * vel.vx + dy * vel.vy) / (len * Math.hypot(vel.vx, vel.vy)) : 0;
      return { x, y, score: along * 200 - len };
    }).sort((a, b) => b.score - a.score);
    for (const e of exits.slice(0, 2)) steps.push({ x: e.x, y: e.y, kind: 'door', look: 0.5 });
  }
  for (let i = 0; i < 2; i++) {
    const a = g.rng.float(0, TAU), r = g.rng.float(30, 80);
    steps.push({ x: origin.x + Math.cos(a) * r, y: origin.y + Math.sin(a) * r, kind: 'wander', look: 0.4 });
  }
  return steps;
}

function beginSearch(g: Game, m: Monster, f: Floor, seconds: number, planned: boolean): void {
  m.state = 'search';
  m.searchTimer = seconds;
  m.searchStep = null; m.lookTimer = 0; m.searchCued = false; m.path = null; m.wait = 0;
  m.searchPlan = planned ? planSearch(g, m, f, { x: m.x, y: m.y }, m.seenVel) : null;
  m.poi = null;
}

/** Standing at a hiding place, it reaches in: anyone hidden there is found. */
function checkCover(g: Game, m: Monster, f: Floor, step: SearchStep): boolean {
  const p = f.props.find((p) => p.id === step.prop);
  if (!p || !p.occupants.length) return false;
  const victim = p.occupants.map((id) => g.chars.get(id)).find((c) => c && c.state === 'hidden');
  if (!victim || m.carrying.length >= 2) return false;
  g.unhide(victim);
  g.say(`숨은 곳을 들켰다! ${p.kind.hide?.label ?? '거기'}에서 끌려 나왔다!`, 3);
  g.capture(victim);
  return true;
}

export function updateMonster(g: Game, dt: number): void {
  const m = g.monster;
  m.anim += dt;
  m.sightAge += dt;
  m.passageCooldown = Math.max(0, m.passageCooldown - dt);
  m.passageCheck -= dt;
  m.footstepCue = Math.max(0, m.footstepCue - dt);
  m.growl = Math.max(0, m.growl - dt);
  if (m.attackTimer > 0) m.attackTimer -= dt;
  if (m.lightSeen > 0) m.lightSeen -= dt;
  if (m.directBlock > 0) m.directBlock -= dt;
  if (updatePassage(g, m, dt)) {
    for (const id of m.carrying) {
      const c = g.chars.get(id);
      if (c) { c.floor = m.floor; c.x = m.x; c.y = m.y; }
    }
    return;
  }
  const f = g.mansion.get(m.floor);
  const segs = g.occluders(f);

  if (m.state === 'stunned') {
    m.stun -= dt;
    if (m.stun <= 0) {
      beginSearch(g, m, f, 6, false);
      m.poi = { floor: m.floor, x: m.x, y: m.y };
    }
    return;
  }

  // ---- senses ----
  sense(g, m, f, segs, dt);
  updatePacing(g, m, dt);

  // ---- state behaviour ----
  if (m.doorWait) {
    if (m.doorWait.floor !== m.floor || dist(m.x, m.y, m.doorWait.x, m.doorWait.y) > 40) m.doorWait = null;
    else if (m.doorWait.t > 0) { m.doorWait.t = Math.max(0, m.doorWait.t - dt); return; }
  }
  switch (m.state) {
    case 'patrol': {
      if (!m.poi) m.poi = pickPatrol(g, m);
      if (m.wait > 0) { m.wait -= dt; break; }
      const result = goTo(g, m, f, m.poi, MON.patrol * m.speedMul, dt);
      if (result !== 'moving') {
        m.path = null;
        m.wait = result === 'arrived' ? g.rng.float(1.5, 4) : 0.5;
        m.poi = pickPatrol(g, m);
        if (m.poi.floor === 'attic' && g.mansion.get('attic').roomById.has('lair') && roomAtPx(f, m.x, m.y)?.id === 'lair') {
          m.state = 'lair';
          m.wait = g.rng.float(25, 45);
        }
      }
      break;
    }
    case 'lair': {
      m.wait -= dt;
      if (m.wait <= 0) { m.state = 'patrol'; m.poi = pickPatrol(g, m); }
      break;
    }
    case 'investigate': {
      if (!m.poi) { m.state = 'patrol'; break; }
      const result = goTo(g, m, f, m.poi, MON.investigate * m.speedMul, dt);
      if (result !== 'moving') {
        beginSearch(g, m, f, result === 'arrived' ? 5 : 3, false);
        if (result === 'unreachable') m.poi = { floor: m.floor, x: m.x, y: m.y };
      }
      break;
    }
    case 'search': {
      m.searchTimer -= dt;
      if (m.searchTimer <= 0) {
        m.state = m.carrying.length ? 'carry' : 'patrol'; m.poi = m.carrying.length ? null : pickPatrol(g, m);
        m.path = null; m.searchPlan = null; m.searchStep = null;
        break;
      }
      if (m.lookTimer > 0) {
        // standing still, looking (or reaching into cover)
        m.lookTimer -= dt;
        if (m.lookTimer <= 0) {
          const step = m.searchStep;
          m.searchStep = null; m.poi = null;
          if (step?.kind === 'hide' && checkCover(g, m, f, step)) break;
        }
        break;
      }
      if (!m.poi && m.searchPlan?.length) {
        const step = m.searchPlan.shift()!;
        m.searchStep = step;
        m.poi = { floor: m.floor, x: step.x, y: step.y };
        m.path = null;
        if (step.kind === 'hide' && !m.searchCued) {
          m.searchCued = true;
          const listener = g.leader;
          if (listener?.floor === m.floor && dist(listener.x, listener.y, m.x, m.y) < 300) { g.say('괴물이 숨을 만한 곳을 뒤진다…', 2.5); if (m.growl <= 0) { g.audio.growl(0.35); m.growl = 3; } }
        }
      }
      if (!m.poi || (!m.searchStep && m.wait <= 0)) {
        // wander around the point of interest
        const base = m.lastSeen && m.lastSeen.floor === m.floor ? m.lastSeen : { x: m.x, y: m.y };
        const a = g.rng.float(0, TAU), r = g.rng.float(20, 70);
        m.poi = { floor: m.floor, x: base.x + Math.cos(a) * r, y: base.y + Math.sin(a) * r };
        m.wait = g.rng.float(0.8, 1.6);
        m.path = null;
      }
      m.wait -= dt;
      const result = goTo(g, m, f, m.poi, MON.search * m.speedMul, dt);
      if (result === 'unreachable') {
        m.poi = null; m.searchStep = null; m.wait = 0; m.path = null;
      } else if (result === 'arrived' && m.searchStep) {
        m.lookTimer = m.searchStep.look;
        m.path = null;
      }
      break;
    }
    case 'chase': {
      const t = m.target ? g.chars.get(m.target) : null;
      if (!t || !isChaseable(t) || m.sightAge > 0.15) {
        // lost target
        if (t && t.state === 'hidden' && t.hideProp && t.hideProp === m.witnessedHide && t.floor === m.floor) {
          // saw them hide: pull them out
          m.state = 'pull';
          m.pullProp = t.hideProp;
          m.poi = { floor: t.floor, x: t.x, y: t.y + 10 };
          m.path = null;
          break;
        }
        // Follow only the last observation, never an unseen actor's live position: it presses on
        // toward where the target was heading for a moment, then searches where the trail ended.
        const goal = m.sightAge <= MON.pursuit ? predictedPoint(m) : m.lastSeen;
        if (goal && goTo(g, m, f, goal, MON.chase * m.speedMul, dt) === 'moving') break;
        m.target = null; m.witnessedHide = null;
        beginSearch(g, m, f, 12, !!m.lastSeen && m.lastSeen.floor === m.floor);
        break;
      }
      if (t.floor !== m.floor) {
        // A witnessed stair transition records its landing, not the player's later route.
        if (m.lastSeen && goTo(g, m, f, m.lastSeen, MON.chase * m.speedMul, dt) === 'moving') break;
        m.target = null;
        beginSearch(g, m, f, 9, !!m.lastSeen && m.lastSeen.floor === m.floor);
        break;
      }
      const speed = (m.carrying.length ? MON.carry : MON.chase) * m.speedMul;
      const d = dist(m.x, m.y, t.x, t.y);
      const los = hasLos(m.x, g.eyeY(m), t.x, g.eyeY(t), segs);
      if (los && d < MON.catchDist + 2 && m.carrying.length < 2) {
        g.capture(t);
        break;
      }
      if (los && m.carrying.length && d < MON.swipeDist && m.attackTimer <= 0) {
        g.swipe(t);
        m.attackTimer = 1.4;
      }
      if (m.carrying.length >= 2) { m.state = 'carry'; m.target = null; m.path = null; break; }
      if (los && d < 90 && m.directBlock <= 0) {
        if (moveDirect(g, m, f, t.x, t.y, speed, dt)) { m.directBlock = 0.9; m.path = null; }
      } else if (m.lastSeen) goTo(g, m, f, m.lastSeen, speed, dt);
      break;
    }
    case 'pull': {
      if (!m.poi) { m.state = 'patrol'; break; }
      const p = f.props.find((p) => p.id === m.pullProp);
      if (!p || p.occupants.length === 0) { beginSearch(g, m, f, 4, false); break; }
      const result = goTo(g, m, f, m.poi, MON.chase * m.speedMul, dt);
      if (result === 'unreachable') {
        m.pullProp = null; beginSearch(g, m, f, 4, false);
      } else if (result === 'arrived') {
        const victim = g.chars.get(p.occupants[0])!;
        g.unhide(victim);
        g.say(`${p.kind.hide?.label ?? '거기'}에서 끌려 나왔다!`, 3);
        g.capture(victim);
        m.pullProp = null;
      }
      break;
    }
    case 'carry': {
      const prison = g.activePrisonFor(m);
      if (!prison) { m.state = 'patrol'; break; }
      const pf = g.mansion.get(prison.floor);
      const room = pf.roomById.get(prison.room)!;
      const target = { floor: prison.floor, x: (room.x + room.w / 2) * TILE, y: (room.y + room.h / 2) * TILE };
      const here = roomAtPx(pf, m.x, m.y);
      if (m.floor === prison.floor && here?.id === prison.room) {
        g.imprison(m, prison);
        m.state = 'patrol';
        m.poi = pickPatrol(g, m);
        m.path = null;
        break;
      }
      goTo(g, m, f, target, MON.carry * m.speedMul, dt);
      if (m.floor !== f.id || m.transit) break;
      // a rescuer who gets too close is grabbed too (it can hold two), otherwise swiped at
      for (const c of g.chars.values()) {
        if (c.floor !== m.floor || !isChaseable(c)) continue;
        const d = dist(m.x, m.y, c.x, c.y);
        if (!hasLos(m.x, g.eyeY(m), c.x, g.eyeY(c), segs)) continue;
        if (d < MON.catchDist && m.carrying.length < 2) { g.capture(c); m.state = 'carry'; continue; }
        if (d < MON.swipeDist && m.attackTimer <= 0) { g.swipe(c); m.attackTimer = 1.4; }
      }
      break;
    }
  }
  // carried friends ride along
  for (const id of m.carrying) {
    const c = g.chars.get(id)!;
    c.floor = m.floor; c.x = m.x; c.y = m.y;
  }
}

function isChaseable(c: Character): boolean {
  return c.state === 'free' || c.state === 'groggy';
}

function travelMonster(g: Game, m: Monster, toFloor: FloorId, stairsId: string): void {
  const tf = g.mansion.get(toFloor);
  const s = tf.stairs.find((s) => s.def.id === stairsId);
  if (!s) return;
  m.floor = toFloor;
  m.x = tileCenter(s.entry.x);
  m.y = tileCenter(s.entry.y);
  m.path = null;
  m.pathTimer = 0;
  m.passagePlan = null;
  m.doorWait = null;
}

type TravelResult = 'moving' | 'arrived' | 'unreachable';
interface NavigationGoal { floor: FloorId; tx: number; ty: number; x: number; y: number; snapped: boolean }
const navigationGoals = new WeakMap<Monster, NavigationGoal>();

/** Patrol and investigations use stairs too; changing the target floor must never freeze AI. */
function goTo(g: Game, m: Monster, f: Floor, poi: { floor: FloorId; x: number; y: number }, speed: number, dt: number, exact = false): TravelResult {
  if (!exact && m.passageCooldown <= 0 && m.state !== 'search' && m.state !== 'pull') {
    if (m.passagePlan && (m.passagePlan.goal.floor !== poi.floor || dist(m.passagePlan.goal.x, m.passagePlan.goal.y, poi.x, poi.y) > 64)) {
      m.passagePlan = null; m.passageCheck = 0;
    }
    if (!m.passagePlan && m.passageCheck <= 0) { m.passagePlan = planPassage(g, m, poi, speed); m.passageCheck = 1.5; }
    if (m.passagePlan) {
      const entry = passageEntry(m.passagePlan.from);
      const result = goTo(g, m, f, entry, speed, dt, true);
      if (result === 'arrived') { enterPassage(g, m); return 'moving'; }
      if (result === 'moving') return result;
      m.passagePlan = null; m.passageCheck = 3;
    }
  }
  if (poi.floor !== m.floor) {
    const stairs = stairsToward(g.mansion, m.floor, poi.floor);
    if (!stairs) return 'unreachable';
    const result = goTo(g, m, f, { floor: m.floor, x: tileCenter(stairs.far.x), y: tileCenter(stairs.far.y) }, speed, dt, true);
    if (result === 'arrived') {
      travelMonster(g, m, stairs.def.toFloor, stairs.def.to);
      return 'moving';
    }
    return result;
  }
  const radius = exact ? 3 : 8;
  if (dist(m.x, m.y, poi.x, poi.y) < radius) return 'arrived';
  m.pathTimer -= dt;
  const tx = tileOf(poi.x), ty = tileOf(poi.y);
  let goal = navigationGoals.get(m);
  if (!m.path || m.pathTimer <= 0 || !goal || goal.floor !== f.id || goal.tx !== tx || goal.ty !== ty) {
    const passDoor = (x: number, y: number) => f.doorAt.get(y * f.w + x)?.kind !== 'exit';
    m.path = findPath(f, tileOf(m.x), tileOf(m.y), tx, ty, passDoor, 8000);
    m.pathTimer = 0.5;
    if (!m.path) return 'unreachable';
    const last = m.path[m.path.length - 1];
    const snapped = !boxFree(f, poi.x, poi.y, d => d.kind !== 'exit', 6, 4);
    if (exact && snapped) { m.path = null; return 'unreachable'; }
    goal = { floor: f.id, tx, ty, x: snapped ? tileCenter(last[0]) : poi.x, y: snapped ? tileCenter(last[1]) : poi.y, snapped };
    navigationGoals.set(m, goal);
  }
  // Do not skip a corner while still several pixels off the centre of a narrow doorway.
  while (m.path.length && dist(m.x, m.y, tileCenter(m.path[0][0]), tileCenter(m.path[0][1])) < 2.5) m.path.shift();
  if (!m.path.length) {
    const gx = goal.snapped ? goal.x : poi.x, gy = goal.snapped ? goal.y : poi.y;
    if (dist(m.x, m.y, gx, gy) < radius) return 'arrived';
    const blocked = moveDirect(g, m, f, gx, gy, speed, dt);
    if (blocked && dist(m.x, m.y, gx, gy) >= radius) return 'unreachable';
    return dist(m.x, m.y, gx, gy) < radius ? 'arrived' : 'moving';
  }
  const [nx, ny] = m.path[0];
  moveDirect(g, m, f, tileCenter(nx), tileCenter(ny), speed, dt);
  return 'moving';
}

function moveDirect(g: Game, m: Monster, f: Floor, tx: number, ty: number, speed: number, dt: number): boolean {
  const dx = tx - m.x, dy = ty - m.y;
  const d = Math.hypot(dx, dy);
  if (d < 0.01) return false;
  const step = Math.min(d, speed * dt);
  const mx = (dx / d) * step, my = (dy / d) * step;
  const oldX = m.x, oldY = m.y;
  const blocked = moveBox(f, m, mx, my, monsterDoorRule(g), 6, 4);
  m.footstepDistance += dist(oldX, oldY, m.x, m.y);
  if (m.footstepDistance >= 26) {
    m.footstepDistance %= 26;
    const listener = g.leader;
    if (listener && listener.floor === m.floor && ['free', 'groggy', 'hidden'].includes(listener.state)) {
      const distance = dist(listener.x, listener.y, m.x, m.y);
      if (distance < 360) {
        const muffled = !hasLos(listener.x, g.eyeY(listener), m.x, g.eyeY(m), g.occluders(f));
        const volume = (1 - distance / 360) * (muffled ? 0.4 : 1);
        g.audio.monsterStep(Math.max(-1, Math.min(1, (m.x - listener.x) / 180)), volume, muffled);
        if (distance < 280 && m.footstepCue <= 0 && !g.message) {
          g.say(muffled ? '벽 너머에서 무거운 발소리가 들린다…' : '가까이서 무거운 발소리가 들린다…', 2);
          m.footstepCue = 18;
        }
      }
    }
  }
  if (blocked) m.pathTimer = 0; // replan sooner
  m.facing = facingFromVec(dx, dy, m.facing);
  return blocked;
}

// ---------- senses ----------
const visScratch: VisResult = { pts: new Float32Array(4096), n: 0 };
const heardNoises = new WeakMap<Monster, WeakSet<Noise>>();

function sense(g: Game, m: Monster, f: Floor, segs: import('@/engine/math').Seg[], dt: number): void {
  const asleep = m.state === 'lair';
  let seeing: Character | null = null;
  let seeDist = 1e9;
  const facingAng = Math.atan2(m.facing === 'down' ? 1 : m.facing === 'up' ? -1 : 0, m.facing === 'right' ? 1 : m.facing === 'left' ? -1 : 0);
  for (const c of g.chars.values()) {
    if (c.floor !== m.floor || !isChaseable(c)) continue;
    const d = dist(m.x, m.y, c.x, c.y);
    if (d > 320) continue;
    const los = hasLos(m.x, g.eyeY(m), c.x, g.eyeY(c), segs);
    if (!los) continue;
    let detect = false;
    if (!asleep) {
      if (c.flashOn) {
        if (d < 240) detect = true;
        else {
          // is the monster inside the beam?
          const vis = visibility(c.x, g.eyeY(c), 170 * (c.def.trait.lightRangeMul ?? 1), segs, { center: c.flashAngle, half: 0.42 }, visScratch);
          if (pointInPolygon(m.x, m.y - 10, vis.pts, vis.n)) detect = true;
        }
      }
      if (!detect) {
        const lit = g.isLitByStatic(c);
        const inCone = Math.abs(angleDiff(facingAng, Math.atan2(c.y - m.y, c.x - m.x))) < 1.25;
        if (lit && d < 150 && inCone) detect = true;
        else if (d < 42 && (inCone || d < 24)) detect = true;
      }
    } else if (d < 30) detect = true;
    if (detect && d < seeDist) { seeing = c; seeDist = d; }
  }
  if (seeing) {
    if (m.state !== 'chase' || m.target === seeing.id || seeDist < 60) {
      // Two sightings close together give a velocity the pursuit can extrapolate.
      const prev = m.lastSeen, gap = m.seenAt === null ? Infinity : g.time - m.seenAt;
      if (prev && prev.floor === m.floor && gap > 0.01 && gap < 0.6) {
        const vx = (seeing.x - prev.x) / gap, vy = (seeing.y - prev.y) / gap;
        m.seenVel = m.seenVel ? { vx: m.seenVel.vx * 0.5 + vx * 0.5, vy: m.seenVel.vy * 0.5 + vy * 0.5 } : { vx, vy };
      } else m.seenVel = null;
      m.lastSeen = { floor: m.floor, x: seeing.x, y: seeing.y };
      m.seenAt = g.time;
      m.sightAge = 0;
    }
    m.lastContact = { floor: m.floor, x: seeing.x, y: seeing.y, t: g.time };
    if (seeing.flashOn) m.lightSeen = 1.5;
    const instant = seeDist < 60 || m.state === 'chase' || m.state === 'carry';
    m.aware = Math.min(1, m.aware + (instant ? 1 : dt * 1.6));
    if (m.aware >= 1 && m.state !== 'chase' && m.state !== 'pull' && m.carrying.length < 2) {
      if (m.state === 'lair') g.say('둥지의 무언가가 눈을 떴다...', 3);
      m.state = 'chase';
      m.target = seeing.id;
      m.witnessedHide = null;
      m.path = null;
      if (m.growl <= 0) { g.audio.growl(); m.growl = 2.5; g.onMonsterSpotted(seeing); }
    } else if (m.state === 'chase' && m.target !== seeing.id && seeDist < 60) {
      m.target = seeing.id;
      m.witnessedHide = null;
    }
  } else {
    m.aware = Math.max(0, m.aware - dt * 0.5);
  }
  // hearing
  if (m.state === 'chase' || m.state === 'pull') return;
  let heard = heardNoises.get(m);
  if (!heard) { heard = new WeakSet<Noise>(); heardNoises.set(m, heard); }
  let strongest: Noise | null = null, strength = -Infinity;
  for (const n of g.noises) {
    if (heard.has(n)) continue;
    heard.add(n); // one impulse, one hearing decision; frame rate cannot multiply suspicion
    if (n.t < 0.45) continue;
    if (n.from === 'monster') continue;
    const sameFloor = n.floor === m.floor;
    const d = sameFloor ? dist(m.x, m.y, n.x, n.y) : stairSoundDistance(g, m, n);
    const los = !sameFloor || hasLos(m.x, g.eyeY(m), n.x, n.y, segs);
    const r = n.r * (los ? 1 : 0.55) * (asleep ? 0.5 : 1) * MON.hearMul;
    if (d > r) continue;
    // loud noise close by = immediate; footsteps raise suspicion
    if (n.kind === 'step') {
      m.aware = Math.min(1, m.aware + 0.35);
      if (m.aware < 0.7 && m.state !== 'search') continue;
    }
    if (m.state === 'carry') continue;
    if (m.state === 'lair' && n.kind === 'step' && d > 60) continue;
    const score = r - d + (n.kind === 'step' ? 0 : 80);
    if (score > strength) { strongest = n; strength = score; }
  }
  if (strongest) {
    m.state = 'investigate'; m.wait = 0;
    m.poi = { floor: strongest.floor, x: strongest.x, y: strongest.y };
    m.lastSeen = null; m.seenVel = null; m.path = null;
    m.searchPlan = null; m.searchStep = null; m.lookTimer = 0;
    if (strongest.kind !== 'step') m.lastContact = { floor: strongest.floor, x: strongest.x, y: strongest.y, t: g.time };
    if (strongest.kind !== 'step' && m.growl <= 0) {
      const listener = g.leader;
      if (listener?.floor === m.floor && dist(listener.x, listener.y, m.x, m.y) < 360) g.audio.growl(0.5);
      else if (listener?.floor === strongest.floor && dist(listener.x, listener.y, strongest.x, strongest.y) < 120) g.audio.growl(0.18);
      m.growl = 3;
    }
  }
}

/** Called before an actor disappears into furniture or onto another floor. */
export function monsterWitnesses(g: Game, c: Character): boolean {
  const m = g.monster;
  return m.state === 'chase' && m.target === c.id && m.floor === c.floor && m.sightAge < 0.25 &&
    !!m.lastSeen && dist(m.lastSeen.x, m.lastSeen.y, c.x, c.y) < 32 &&
    hasLos(m.x, g.eyeY(m), c.x, g.eyeY(c), g.occluders(g.floorOf(c)));
}

export function monsterEyesVisible(g: Game, from: Character): boolean {
  const m = g.monster;
  if (m.floor !== from.floor) return false;
  if (m.state === 'lair') return false;
  if (passageOpacity(m) < 0.35) return false;
  const f = g.mansion.get(m.floor);
  if (dist(m.x, m.y, from.x, from.y) > 520) return false;
  return hasLos(from.x, g.eyeY(from), m.x, g.eyeY(m), g.occluders(f));
}
